import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useIsNewEntry } from "../../hooks";
import { useDispatch, useSelector } from "../../redux/hooks";
import {
  setPersistInlineSave,
  setTriggerInlineSave,
} from "../../redux/slices/generatorSlice";
/**
 * Custom hook to determine if a save operation is required for a specific section.
 *
 * @param sectionsClassName - The class name of the sections to monitor for changes.
 * @param inlineSaving - A boolean indicating if inline saving is enabled.
 * @param sectionIndex - The index of the section being monitored.
 * @returns A boolean indicating whether a save is required.
 */
const useIsSaveRequired = (
  sectionsClassName: string,
  inlineSaving: boolean,
  sectionIndex: number,
) => {
  const dispatch = useDispatch();
  const [isSaveRequired, setIsSaveRequired] = useState(false);
  const { lbeidOrPartId } = useParams();
  const isNewEntry = useIsNewEntry();
  const { noSaves } = useSelector(state => state.entrySlice);
  const { dynamicRowAdded, triggerInlineSave, forceTurnOffInlineSave } =
    useSelector(state => state.generatorSlice);

  useEffect(() => {
    if (!inlineSaving) return;

    const handleInputChange = (event: any) => {
      const target = event.target as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement;
      if (
        target.matches(
          `.${sectionsClassName} input, .${sectionsClassName} select, .${sectionsClassName} textarea`,
        )
      ) {
        setIsSaveRequired(true);

        if (isNewEntry) dispatch(setPersistInlineSave(sectionIndex));
      }
    };

    const container = document.querySelector(`.${sectionsClassName}`);
    container?.addEventListener("change", handleInputChange);

    return () => {
      container?.removeEventListener("change", handleInputChange);
    };
  }, [inlineSaving, sectionsClassName, isNewEntry]);

  useEffect(() => {
    setIsSaveRequired(false);
  }, [noSaves, lbeidOrPartId, isNewEntry, forceTurnOffInlineSave]);

  useEffect(() => {
    if (
      dynamicRowAdded === sectionIndex ||
      triggerInlineSave === sectionIndex
    ) {
      setIsSaveRequired(true);
      if (isNewEntry) dispatch(setPersistInlineSave(sectionIndex));
    }
    if (triggerInlineSave !== -1) {
      dispatch(setTriggerInlineSave(-1));
    }
  }, [dynamicRowAdded, sectionIndex, triggerInlineSave, isNewEntry]);

  return isSaveRequired;
};

export default useIsSaveRequired;
